import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../lib/api';
import { ArrowLeft, Upload, Trash, DownloadSimple, Lightning, CheckCircle, CircleNotch, Warning, FileImage, FilePdf, Eye, FileXls, X, Blueprint, Cube, CaretDown, CaretUp, FileDotsSolid, Play } from '@phosphor-icons/react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProjectDetail() {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [files, setFiles] = useState([]);
  const [boqData, setBoqData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState({});
  const [dragOver, setDragOver] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [dxfMeta, setDxfMeta] = useState({});
  const [batchAnalyzing, setBatchAnalyzing] = useState(false);
  const [filePrompts, setFilePrompts] = useState({});
  const [activeTab, setActiveTab] = useState('files');

  const fetchData = useCallback(async () => {
    try {
      const [projRes, filesRes, boqRes] = await Promise.all([
        api.get(`/projects/${projectId}`),
        api.get(`/files/${projectId}`),
        api.get(`/boq/${projectId}`),
      ]);
      setProject(projRes.data);
      setFiles(filesRes.data);
      setBoqData(boqRes.data);
    } catch (err) {
      console.error('Failed to fetch project data:', err);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleUpload = async (fileList) => {
    if (!fileList?.length) return;
    setUploading(true);
    try {
      for (const file of fileList) {
        const formData = new FormData();
        formData.append('file', file);
        await api.post(`/upload/${projectId}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      toast.success(`${fileList.length} file(s) uploaded successfully`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleAnalyze = async (fileId) => {
    setAnalyzing(prev => ({ ...prev, [fileId]: true }));
    try {
      const prompt = filePrompts[fileId] || '';
      const analyzeRes = await api.post(`/ai/analyze/${fileId}`, { user_prompt: prompt });
      toast.success('Analysis complete! Switching to BOQ tab to view results.', { duration: 5000 });
      // Fetch fresh data and switch tab after state is updated
      const [projRes, filesRes, boqRes] = await Promise.all([
        api.get(`/projects/${projectId}`),
        api.get(`/files/${projectId}`),
        api.get(`/boq/${projectId}`),
      ]);
      setProject(projRes.data);
      setFiles(filesRes.data);
      setBoqData(boqRes.data);
      // Switch tab after data is set
      setTimeout(() => setActiveTab('boq'), 100);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Analysis failed');
    } finally {
      setAnalyzing(prev => ({ ...prev, [fileId]: false }));
    }
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm('Delete this file?')) return;
    try {
      await api.delete(`/files/remove/${fileId}`);
      toast.success('File deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete file');
    }
  };

  const handleExport = async () => {
    try {
      const res = await api.get(`/boq/${projectId}/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `BOQ_${project?.name || 'export'}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel exported');
    } catch (err) {
      toast.error('Export failed');
    }
  };

  const handlePreview = async (fileId) => {
    if (previewFile === fileId) {
      setPreviewFile(null);
      return;
    }
    setPreviewFile(fileId);
  };

  const handleFetchMeta = async (fileId) => {
    if (dxfMeta[fileId]) {
      setDxfMeta(prev => { const n = { ...prev }; delete n[fileId]; return n; });
      return;
    }
    try {
      const res = await api.get(`/files/metadata/${fileId}`);
      setDxfMeta(prev => ({ ...prev, [fileId]: res.data.metadata }));
    } catch {
      toast.error('Failed to load metadata');
    }
  };

  const handleBatchAnalyze = async () => {
    setBatchAnalyzing(true);
    try {
      const res = await api.post(`/ai/analyze-batch/${projectId}`);
      const { succeeded, total } = res.data;
      toast.success(`Batch analysis complete: ${succeeded}/${total} files analyzed successfully`, { duration: 5000 });
      const [projRes, filesRes, boqRes] = await Promise.all([
        api.get(`/projects/${projectId}`),
        api.get(`/files/${projectId}`),
        api.get(`/boq/${projectId}`),
      ]);
      setProject(projRes.data);
      setFiles(filesRes.data);
      setBoqData(boqRes.data);
      setTimeout(() => setActiveTab('boq'), 100);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Batch analysis failed');
    } finally {
      setBatchAnalyzing(false);
    }
  };

  const handleExportPdf = async () => {
    try {
      const res = await api.get(`/boq/${projectId}/export-pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `BOQ_${project?.name || 'export'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF exported');
    } catch (err) {
      toast.error('PDF export failed');
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleUpload(e.dataTransfer.files);
  };

  const getFileIcon = (type, filename) => {
    if (type === 'application/pdf') return <FilePdf size={24} weight="regular" className="text-[#FF2B2B]" />;
    if (type === 'application/dxf' || filename?.endsWith('.dxf')) return <Blueprint size={24} weight="regular" className="text-[#FF5A00]" />;
    if (type === 'application/dwg' || filename?.endsWith('.dwg')) return <Cube size={24} weight="regular" className="text-[#0047FF]" />;
    return <FileImage size={24} weight="regular" className="text-[#0047FF]" />;
  };

  const getStatusBadge = (status) => {
    const map = {
      pending: { bg: 'bg-black/5 text-[#8F8F8F] border-black/10', icon: CircleNotch, label: 'Pending' },
      processing: { bg: 'bg-[#FFC107]/10 text-[#FFC107] border-[#FFC107]/30', icon: CircleNotch, label: 'Processing' },
      completed: { bg: 'bg-[#00A859]/10 text-[#00A859] border-[#00A859]/30', icon: CheckCircle, label: 'Completed' },
      error: { bg: 'bg-[#FF2B2B]/10 text-[#FF2B2B] border-[#FF2B2B]/30', icon: Warning, label: 'Error' },
    };
    const s = map[status] || map.pending;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider border ${s.bg}`}>
        <s.icon size={12} weight={status === 'processing' ? 'bold' : 'regular'} className={status === 'processing' ? 'animate-spin' : ''} />
        {s.label}
      </span>
    );
  };

  const allItems = boqData?.results?.flatMap(r => r.items || []) || [];
  const pendingCount = files.filter(f => f.analysis_status === 'pending' || f.analysis_status === 'error').length;

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-black/5 w-64" />
          <div className="h-64 bg-white border border-black/15" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8" data-testid="project-detail-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <Link to="/projects" data-testid="back-to-projects" className="inline-flex items-center gap-1 font-mono text-xs uppercase tracking-wide text-[#5E5E5E] hover:text-[#FF5A00] mb-4 transition-colors">
            <ArrowLeft size={14} weight="bold" /> Back to Projects
          </Link>
          <h1 className="font-sans text-3xl font-black tracking-tight">{project?.name}</h1>
          {project?.description && (
            <p className="font-mono text-sm text-[#5E5E5E] mt-1">{project.description}</p>
          )}
        </div>
        <div className="flex items-center gap-3">
          {pendingCount > 0 && (
            <button
              onClick={handleBatchAnalyze}
              disabled={batchAnalyzing}
              data-testid="batch-analyze-btn"
              className="inline-flex items-center gap-2 bg-[#FF5A00] text-white px-6 py-3 font-mono text-sm uppercase tracking-wide hover:bg-[#E04E00] transition-all duration-150 hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none shadow-[4px_4px_0px_rgba(0,0,0,1)] disabled:opacity-50 disabled:translate-x-0 disabled:translate-y-0"
            >
              {batchAnalyzing ? (
                <><CircleNotch size={16} weight="bold" className="animate-spin" /> Analyzing...</>
              ) : (
                <><Play size={16} weight="bold" /> Analyze All ({pendingCount})</>
              )}
            </button>
          )}
          {allItems.length > 0 && (
            <>
              <button
                onClick={handleExport}
                data-testid="export-boq-btn"
                className="inline-flex items-center gap-2 bg-black text-white px-5 py-3 font-mono text-sm uppercase tracking-wide hover:bg-black/80 transition-all duration-150 hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none shadow-[4px_4px_0px_rgba(255,90,0,1)]"
              >
                <FileXls size={16} weight="bold" /> Excel
              </button>
              <button
                onClick={handleExportPdf}
                data-testid="export-pdf-btn"
                className="inline-flex items-center gap-2 border-2 border-black text-black px-5 py-3 font-mono text-sm uppercase tracking-wide hover:bg-black hover:text-white transition-all duration-150"
              >
                <FilePdf size={16} weight="bold" /> PDF
              </button>
            </>
          )}
        </div>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-transparent border-b border-black/15 rounded-none h-auto p-0 w-full justify-start">
          <TabsTrigger
            value="files"
            data-testid="tab-files"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#FF5A00] data-[state=active]:text-[#FF5A00] data-[state=active]:bg-transparent px-6 py-3 font-mono text-sm uppercase tracking-wide"
          >
            Files ({files.length})
          </TabsTrigger>
          <TabsTrigger
            value="boq"
            data-testid="tab-boq"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#FF5A00] data-[state=active]:text-[#FF5A00] data-[state=active]:bg-transparent px-6 py-3 font-mono text-sm uppercase tracking-wide"
          >
            BOQ ({allItems.length} items)
          </TabsTrigger>
        </TabsList>

        {/* Files Tab */}
        <TabsContent value="files" className="mt-6">
          {/* Upload Zone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            data-testid="upload-dropzone"
            className={`border-2 border-dashed p-12 text-center mb-6 transition-all ${
              dragOver ? 'border-[#FF5A00] bg-[#FF5A00]/5' : 'border-black/30 bg-white hover:border-black/50'
            }`}
          >
            <Upload size={40} weight="regular" className={`mx-auto mb-4 ${dragOver ? 'text-[#FF5A00]' : 'text-[#8F8F8F]'}`} />
            <p className="font-sans text-lg font-bold mb-2">
              {uploading ? 'Uploading...' : 'Drop files here or click to upload'}
            </p>
            <p className="font-mono text-xs text-[#8F8F8F] mb-4">Supports PNG, JPEG, WEBP, PDF, DXF, DWG (max 50MB)</p>
            <label
              htmlFor="file-upload"
              data-testid="upload-file-btn"
              className="inline-flex items-center gap-2 bg-[#FF5A00] text-white px-6 py-3 font-mono text-sm uppercase tracking-wide cursor-pointer hover:bg-[#E04E00] transition-colors"
            >
              <Upload size={16} weight="bold" /> Select Files
            </label>
            <input
              id="file-upload"
              type="file"
              multiple
              accept="image/png,image/jpeg,image/webp,application/pdf,.dxf,.dwg"
              className="hidden"
              onChange={(e) => handleUpload(e.target.files)}
              data-testid="file-input"
            />
          </div>

          {/* File List */}
          {files.length > 0 ? (
            <div className="border border-black/15 bg-white">
              <div className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-4 p-4 bg-[#F7F7F7] border-b border-black/15 font-mono text-xs uppercase tracking-wider font-bold text-black">
                <span>File</span>
                <span>Type</span>
                <span>Size</span>
                <span>Status</span>
                <span>Actions</span>
              </div>
              {files.map(file => {
                const isDxf = file.file_type === 'application/dxf' || file.filename?.endsWith('.dxf');
                const isDwg = file.file_type === 'application/dwg' || file.filename?.endsWith('.dwg');
                const isCad = isDxf || isDwg;
                const canAnalyze = file.analysis_status === 'pending' || file.analysis_status === 'error';
                return (
                <div key={file.file_id}>
                  <div className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-4 items-center p-4 border-b border-black/10 hover:bg-[#F7F7F7] transition-colors" data-testid={`file-row-${file.file_id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      {getFileIcon(file.file_type, file.filename)}
                      <div className="min-w-0">
                        <span className="font-mono text-sm truncate block">{file.filename}</span>
                        {isCad && (
                          <span className="font-mono text-[10px] text-[#FF5A00] uppercase tracking-wider">
                            {isDwg ? 'AutoCAD DWG' : 'AutoCAD DXF'}
                          </span>
                        )}
                      </div>
                    </div>
                    <span className="font-mono text-xs text-[#8F8F8F] uppercase">{isCad ? (isDwg ? 'dwg' : 'dxf') : file.file_type.split('/')[1]}</span>
                    <span className="font-mono text-xs text-[#8F8F8F]">{(file.file_size / 1024).toFixed(1)} KB</span>
                    {getStatusBadge(file.analysis_status)}
                    <div className="flex items-center gap-2">
                      {canAnalyze && (
                        <button
                          onClick={() => handleAnalyze(file.file_id)}
                          disabled={analyzing[file.file_id]}
                          data-testid={`analyze-btn-${file.file_id}`}
                          className={`inline-flex items-center gap-1 px-3 py-1 font-mono text-[10px] uppercase tracking-wide transition-colors disabled:opacity-50 ${
                            file.analysis_status === 'error'
                              ? 'bg-[#FFC107] text-black hover:bg-[#FFC107]/80'
                              : 'bg-[#FF5A00] text-white hover:bg-[#E04E00]'
                          }`}
                        >
                          <Lightning size={12} weight="bold" />
                          {analyzing[file.file_id] ? 'Analyzing...' : (file.analysis_status === 'error' ? 'Retry' : 'Analyze')}
                        </button>
                      )}
                      {isCad && (
                        <>
                          <button
                            onClick={() => handlePreview(file.file_id)}
                            data-testid={`preview-btn-${file.file_id}`}
                            className={`inline-flex items-center gap-1 px-3 py-1 font-mono text-[10px] uppercase tracking-wide transition-colors border ${
                              previewFile === file.file_id ? 'bg-black text-white border-black' : 'border-black/30 text-[#5E5E5E] hover:border-black hover:text-black'
                            }`}
                          >
                            <Eye size={12} weight="regular" />
                            {previewFile === file.file_id ? 'Hide' : 'Preview'}
                          </button>
                          <button
                            onClick={() => handleFetchMeta(file.file_id)}
                            data-testid={`metadata-btn-${file.file_id}`}
                            className={`inline-flex items-center gap-1 px-3 py-1 font-mono text-[10px] uppercase tracking-wide transition-colors border ${
                              dxfMeta[file.file_id] ? 'bg-[#0047FF] text-white border-[#0047FF]' : 'border-black/30 text-[#5E5E5E] hover:border-black hover:text-black'
                            }`}
                          >
                            <Cube size={12} weight="regular" />
                            {dxfMeta[file.file_id] ? 'Hide' : 'CAD Info'}
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => handleDeleteFile(file.file_id)}
                        data-testid={`delete-file-${file.file_id}`}
                        className="p-1 text-[#8F8F8F] hover:text-[#FF2B2B] transition-colors"
                      >
                        <Trash size={16} weight="regular" />
                      </button>
                    </div>
                  </div>
                  {/* Quantity Question Input */}
                  {canAnalyze && (
                    <div className="border-b border-black/10 bg-[#FAFAFA] px-4 py-3" data-testid={`prompt-row-${file.file_id}`}>
                      <div className="flex items-center gap-3">
                        <div className="flex-1 relative">
                          <input
                            type="text"
                            value={filePrompts[file.file_id] || ''}
                            onChange={(e) => setFilePrompts(prev => ({ ...prev, [file.file_id]: e.target.value }))}
                            data-testid={`prompt-input-${file.file_id}`}
                            className="w-full bg-white border border-black/15 focus:border-[#FF5A00] px-4 py-2.5 pr-10 font-mono text-xs focus:outline-none transition-colors placeholder:text-[#8F8F8F]"
                            placeholder="What quantities do you want to calculate? e.g., Calculate tiles and plastering only..."
                          />
                          <Lightning size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8F8F8F]" />
                        </div>
                        <button
                          onClick={() => handleAnalyze(file.file_id)}
                          disabled={analyzing[file.file_id]}
                          data-testid={`prompt-analyze-btn-${file.file_id}`}
                          className="shrink-0 inline-flex items-center gap-1.5 bg-[#FF5A00] text-white px-5 py-2.5 font-mono text-xs uppercase tracking-wide hover:bg-[#E04E00] transition-colors disabled:opacity-50"
                        >
                          <Lightning size={14} weight="bold" />
                          {analyzing[file.file_id] ? 'Analyzing...' : 'Analyze'}
                        </button>
                      </div>
                      <p className="font-mono text-[10px] text-[#8F8F8F] mt-1.5">Specify the quantities you need, or leave empty for full analysis</p>
                    </div>
                  )}
                  {/* CAD Preview Panel */}
                  {isCad && previewFile === file.file_id && (
                    <div className="border-b border-black/10 bg-[#F7F7F7] p-4" data-testid={`preview-panel-${file.file_id}`}>
                      <p className="font-mono text-xs uppercase tracking-wider font-bold mb-3">CAD Preview (Rendered)</p>
                      <div className="border border-black/15 bg-white p-2 max-h-[500px] overflow-auto">
                        <img
                          src={`${BACKEND_URL}/api/files/preview/${file.file_id}`}
                          alt="DXF Preview"
                          className="w-full h-auto"
                          onError={(e) => { e.target.src = ''; e.target.alt = 'Preview failed to load'; }}
                        />
                      </div>
                    </div>
                  )}
                  {/* CAD Metadata Panel */}
                  {isCad && dxfMeta[file.file_id] && (
                    <div className="border-b border-black/10 bg-[#EBEBEB] p-4" data-testid={`metadata-panel-${file.file_id}`}>
                      <p className="font-mono text-xs uppercase tracking-wider font-bold mb-3">CAD Metadata</p>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {dxfMeta[file.file_id].layers?.length > 0 && (
                          <div>
                            <p className="font-mono text-[10px] uppercase tracking-wider text-[#8F8F8F] mb-1">Layers ({dxfMeta[file.file_id].layers.length})</p>
                            <div className="flex flex-wrap gap-1">
                              {dxfMeta[file.file_id].layers.slice(0, 12).map(l => (
                                <span key={l} className="px-2 py-0.5 bg-white border border-black/10 font-mono text-[10px]">{l}</span>
                              ))}
                              {dxfMeta[file.file_id].layers.length > 12 && (
                                <span className="px-2 py-0.5 font-mono text-[10px] text-[#8F8F8F]">+{dxfMeta[file.file_id].layers.length - 12} more</span>
                              )}
                            </div>
                          </div>
                        )}
                        {dxfMeta[file.file_id].entity_counts && (
                          <div>
                            <p className="font-mono text-[10px] uppercase tracking-wider text-[#8F8F8F] mb-1">Entities</p>
                            <div className="space-y-1">
                              {Object.entries(dxfMeta[file.file_id].entity_counts).slice(0, 8).map(([k, v]) => (
                                <div key={k} className="flex items-center justify-between font-mono text-[10px]">
                                  <span>{k}</span>
                                  <span className="font-bold">{v}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {dxfMeta[file.file_id].text_annotations?.length > 0 && (
                          <div>
                            <p className="font-mono text-[10px] uppercase tracking-wider text-[#8F8F8F] mb-1">Text Annotations</p>
                            <div className="space-y-1 max-h-32 overflow-auto">
                              {dxfMeta[file.file_id].text_annotations.slice(0, 10).map((t, i) => (
                                <p key={i} className="font-mono text-[10px] text-[#5E5E5E] truncate">{t}</p>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      {dxfMeta[file.file_id].total_line_length_units > 0 && (
                        <p className="font-mono text-[10px] text-[#8F8F8F] mt-3">
                          Total line length: <span className="font-bold text-black">{dxfMeta[file.file_id].total_line_length_units.toLocaleString()}</span> drawing units
                          &bull; Total entities: <span className="font-bold text-black">{dxfMeta[file.file_id].total_entities?.toLocaleString()}</span>
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );})}
            </div>
          ) : (
            <div className="bg-white border border-black/15 p-12 text-center">
              <Upload size={48} weight="regular" className="text-[#8F8F8F] mx-auto mb-4" />
              <p className="font-sans text-lg font-bold mb-2">No Files Yet</p>
              <p className="font-mono text-xs text-[#5E5E5E]">Upload construction drawings to start AI analysis</p>
            </div>
          )}
        </TabsContent>

        {/* BOQ Tab */}
        <TabsContent value="boq" className="mt-6">
          {allItems.length > 0 ? (
            <div className="border border-black/15 bg-white" data-testid="boq-table">
              <div className="p-4 bg-[#F7F7F7] border-b border-black/15 flex items-center justify-between">
                <div>
                  <p className="font-sans text-sm font-bold">Bill of Quantities</p>
                  <p className="font-mono text-xs text-[#8F8F8F]">{allItems.length} items from {boqData?.results?.length || 0} analysis</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleExport}
                    data-testid="boq-export-btn"
                    className="inline-flex items-center gap-2 bg-black text-white px-4 py-2 font-mono text-xs uppercase tracking-wide hover:bg-black/80 transition-colors"
                  >
                    <FileXls size={14} weight="bold" /> Excel
                  </button>
                  <button
                    onClick={handleExportPdf}
                    data-testid="boq-export-pdf-btn"
                    className="inline-flex items-center gap-2 border border-black text-black px-4 py-2 font-mono text-xs uppercase tracking-wide hover:bg-black hover:text-white transition-colors"
                  >
                    <FilePdf size={14} weight="bold" /> PDF
                  </button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-[#F7F7F7] border-b border-black/15">
                      <th className="p-4 text-left font-mono text-xs uppercase tracking-wider font-bold text-black">#</th>
                      <th className="p-4 text-left font-mono text-xs uppercase tracking-wider font-bold text-black">Category</th>
                      <th className="p-4 text-left font-mono text-xs uppercase tracking-wider font-bold text-black">Description</th>
                      <th className="p-4 text-left font-mono text-xs uppercase tracking-wider font-bold text-black">Unit</th>
                      <th className="p-4 text-right font-mono text-xs uppercase tracking-wider font-bold text-black">Quantity</th>
                      <th className="p-4 text-left font-mono text-xs uppercase tracking-wider font-bold text-black">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allItems.map((item, i) => (
                      <tr key={i} className="border-b border-black/10 hover:bg-[#F7F7F7] transition-colors" data-testid={`boq-row-${i}`}>
                        <td className="p-4 font-mono text-sm text-[#8F8F8F]">{i + 1}</td>
                        <td className="p-4">
                          <span className="px-2 py-0.5 bg-black/5 font-mono text-xs uppercase tracking-wider">{item.category}</span>
                        </td>
                        <td className="p-4 font-mono text-sm">{item.description}</td>
                        <td className="p-4 font-mono text-sm text-[#5E5E5E]">{item.unit}</td>
                        <td className="p-4 font-mono text-sm text-right font-bold">{item.quantity}</td>
                        <td className="p-4 font-mono text-xs text-[#8F8F8F]">{item.notes}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Summary */}
              {boqData?.results?.map(r => (r.summary && Object.keys(r.summary).length > 0) || r.dxf_metadata || r.user_prompt ? (
                <div key={r.boq_id} className="p-4 border-t border-black/15 bg-[#F7F7F7]">
                  {r.user_prompt && (
                    <div className="mb-3 p-3 bg-[#FF5A00]/5 border border-[#FF5A00]/20">
                      <p className="font-mono text-[10px] uppercase tracking-wider text-[#FF5A00] font-bold mb-1">User Query</p>
                      <p className="font-mono text-xs text-[#050505]">{r.user_prompt}</p>
                    </div>
                  )}
                  <p className="font-mono text-xs font-bold uppercase tracking-wider mb-2">
                    Analysis Summary
                    {r.filename && <span className="font-normal text-[#8F8F8F] ml-2">({r.filename})</span>}
                    {r.dxf_metadata && <span className="ml-2 px-2 py-0.5 bg-[#FF5A00]/10 text-[#FF5A00] text-[10px] uppercase">DXF/CAD</span>}
                  </p>
                  <div className="flex flex-wrap gap-4">
                    {r.elements_detected?.length > 0 && (
                      <span className="font-mono text-xs text-[#5E5E5E]">
                        Elements: {r.elements_detected.join(', ')}
                      </span>
                    )}
                    {r.room_names?.length > 0 && (
                      <span className="font-mono text-xs text-[#5E5E5E]">
                        Rooms: {r.room_names.join(', ')}
                      </span>
                    )}
                    {r.drawing_type && (
                      <span className="font-mono text-xs text-[#5E5E5E]">
                        Type: {r.drawing_type}
                      </span>
                    )}
                  </div>
                  {r.dxf_metadata && (
                    <div className="mt-3 pt-3 border-t border-black/10">
                      <p className="font-mono text-[10px] uppercase tracking-wider text-[#8F8F8F] mb-2">CAD Data Used</p>
                      <div className="flex flex-wrap gap-3">
                        {r.dxf_metadata.layers?.length > 0 && (
                          <span className="font-mono text-[10px] text-[#5E5E5E]">
                            {r.dxf_metadata.layers.length} layers
                          </span>
                        )}
                        {r.dxf_metadata.total_entities > 0 && (
                          <span className="font-mono text-[10px] text-[#5E5E5E]">
                            {r.dxf_metadata.total_entities.toLocaleString()} entities
                          </span>
                        )}
                        {r.dxf_metadata.blocks_used?.length > 0 && (
                          <span className="font-mono text-[10px] text-[#5E5E5E]">
                            {r.dxf_metadata.blocks_used.length} block refs
                          </span>
                        )}
                        {r.dxf_metadata.dimensions?.length > 0 && (
                          <span className="font-mono text-[10px] text-[#5E5E5E]">
                            {r.dxf_metadata.dimensions.length} dimension annotations
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : null)}
            </div>
          ) : (
            <div className="bg-white border border-black/15 p-12 text-center" data-testid="boq-empty">
              <Lightning size={48} weight="regular" className="text-[#8F8F8F] mx-auto mb-4" />
              <p className="font-sans text-lg font-bold mb-2">No BOQ Data Yet</p>
              <p className="font-mono text-xs text-[#5E5E5E] mb-4">Upload drawings and run AI analysis to generate BOQ</p>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
